/**
 * Promo banner config — shared between the home V6PromoCarousel and
 * the /admin/banners editor.
 *
 * Default banners ship 4 colorful variants (ochre / forest / royal /
 * magenta) so the home rail looks vibrant out of the box. The admin
 * page can override copy, icon, theme, CTA, or enabled-state on each
 * row; overrides persist to localStorage under PROMO_STORAGE_KEY so
 * the founder can tweak without a deploy. Production-grade persistence
 * (DB-backed admin_config row) is a follow-up — localStorage is the
 * pragmatic stand-in while we iterate on copy and visuals.
 */

export type ColorTheme = "ochre" | "forest" | "royal" | "magenta" | "ocean" | "ember";

export type PromoIcon =
  | "gift"
  | "users"
  | "trophy"
  | "zap"
  | "sparkles"
  | "flame"
  | "star"
  | "rocket";

/**
 * Per-locale translation of the user-facing strings on a slide.
 * `amount` is currency-adapted (e.g. $25 → R$125 → $500 MXN → ₹2000)
 * so the headline number reads native in every market.
 *
 * Locale keys mirror Granbay's i18n locales (`en`, `pt`, `es`, `fr`).
 */
export interface PromoSlideTranslation {
  eyebrow: string;
  amount: string;
  headline: string;
  body: string;
  ctaLabel: string;
}

export type PromoLocale = "en" | "pt" | "es" | "fr";

/**
 * Regions where this banner is allowed to appear. Empty / undefined
 * = active in every region (default behavior).
 *
 * Region codes match the `regions` table in the API + the
 * `granbay.region` cookie's `region` field. Today's known regions:
 * "global" (US + unrecognized), "brazil", "mexico", "canada",
 * "india". Use those literals; the type stays loose so adding a
 * region later doesn't need a code change here.
 */
export type RegionCode = string;

export interface PromoSlideConfig {
  id: string;
  enabled: boolean;
  // Default / fallback strings (used when no translation matches
  // the user's locale).
  eyebrow: string;
  amount: string;
  headline: string;
  body: string;
  ctaLabel: string;
  ctaHref: string;
  icon: PromoIcon;
  theme: ColorTheme;
  // 2026-05-24 — per-locale overrides. Pick order: visitor's locale
  // → "en" fallback → top-level defaults. PromoCarousel handles the
  // pick at render time.
  translations?: Partial<Record<PromoLocale, PromoSlideTranslation>>;
  // 2026-05-24 — region gating. Empty = show everywhere. Non-empty
  // = only show when visitor's region matches one of the codes.
  regions?: RegionCode[];
}

export interface PromoTheme {
  background: string;
  accent: string;
  bodyColor: string;
  textColor: string;
  amountColor: string;
  iconBg: string;
  iconColor: string;
  borderColor: string;
}

/**
 * Visual themes. Backgrounds use multi-stop linear gradients so each
 * slide reads as a distinct premium hero in the carousel. All themes
 * are theme-neutral (don't bind to var(--color-*) tokens) so they
 * look identical in light + dark mode — same Bet365 / Stake premium
 * hero pattern.
 */
export const THEMES: Record<ColorTheme, PromoTheme> = {
  ochre: {
    background:
      "linear-gradient(120deg, #0a0a0a 0%, #1a1a1a 55%, color-mix(in srgb, #1a1a1a 70%, #b8924f 30%) 100%)",
    accent: "#b8924f",
    bodyColor: "#a1a1a1",
    textColor: "#ededed",
    amountColor: "#ffffff",
    iconBg: "#b8924f",
    iconColor: "#0a0a0a",
    borderColor: "rgba(184, 146, 79, 0.3)",
  },
  forest: {
    background:
      "linear-gradient(120deg, #1a2a1f 0%, #2d3b2e 55%, color-mix(in srgb, #2d3b2e 70%, #15803d 30%) 100%)",
    accent: "#86efac",
    bodyColor: "#a3c9a8",
    textColor: "#f1ece2",
    amountColor: "#ffffff",
    iconBg: "#15803d",
    iconColor: "#f0fdf4",
    borderColor: "rgba(134, 239, 172, 0.3)",
  },
  royal: {
    background:
      "linear-gradient(120deg, #1e1b4b 0%, #312e81 55%, color-mix(in srgb, #312e81 70%, #6366f1 30%) 100%)",
    accent: "#fbbf24",
    bodyColor: "#a5a3d8",
    textColor: "#ede9fe",
    amountColor: "#ffffff",
    iconBg: "#fbbf24",
    iconColor: "#1e1b4b",
    borderColor: "rgba(251, 191, 36, 0.3)",
  },
  magenta: {
    background:
      "linear-gradient(120deg, #4c0519 0%, #831843 55%, color-mix(in srgb, #831843 70%, #ec4899 30%) 100%)",
    accent: "#f9a8d4",
    bodyColor: "#d8b4c8",
    textColor: "#fdf2f8",
    amountColor: "#ffffff",
    iconBg: "#ec4899",
    iconColor: "#fdf2f8",
    borderColor: "rgba(249, 168, 212, 0.3)",
  },
  ocean: {
    background:
      "linear-gradient(120deg, #0c2740 0%, #0e3d59 55%, color-mix(in srgb, #0e3d59 70%, #06b6d4 30%) 100%)",
    accent: "#67e8f9",
    bodyColor: "#94c5d7",
    textColor: "#ecfeff",
    amountColor: "#ffffff",
    iconBg: "#06b6d4",
    iconColor: "#0c2740",
    borderColor: "rgba(103, 232, 249, 0.3)",
  },
  ember: {
    background:
      "linear-gradient(120deg, #451a03 0%, #7c2d12 55%, color-mix(in srgb, #7c2d12 70%, #f97316 30%) 100%)",
    accent: "#fdba74",
    bodyColor: "#d8a896",
    textColor: "#fff7ed",
    amountColor: "#ffffff",
    iconBg: "#f97316",
    iconColor: "#451a03",
    borderColor: "rgba(253, 186, 116, 0.3)",
  },
};

export const COLOR_THEMES: ColorTheme[] = [
  "ochre",
  "forest",
  "royal",
  "magenta",
  "ocean",
  "ember",
];

export const ICON_OPTIONS: PromoIcon[] = [
  "gift",
  "users",
  "trophy",
  "zap",
  "sparkles",
  "flame",
  "star",
  "rocket",
];

export const DEFAULT_SLIDES: PromoSlideConfig[] = [
  {
    id: "monthly-leaderboard",
    enabled: true,
    eyebrow: "Top trader monthly",
    amount: "$500",
    headline: "in the leaderboard pool",
    body: "Top 10 traders split the pool each month.",
    ctaLabel: "View leaderboard →",
    ctaHref: "/promos/monthly-leaderboard",
    icon: "trophy",
    theme: "royal",
    translations: {
      en: {
        eyebrow: "Top trader monthly",
        amount: "$500",
        headline: "in the leaderboard pool",
        body: "Top 10 traders split the pool each month.",
        ctaLabel: "View leaderboard →",
      },
      pt: {
        eyebrow: "Top trader do mês",
        amount: "R$2.500",
        headline: "no prêmio do ranking",
        body: "Top 10 traders dividem o prêmio todo mês.",
        ctaLabel: "Ver ranking →",
      },
      es: {
        eyebrow: "Trader del mes",
        amount: "$10,000 MXN",
        headline: "en el premio del ranking",
        body: "Top 10 traders se reparten el premio cada mes.",
        ctaLabel: "Ver ranking →",
      },
      fr: {
        eyebrow: "Trader du mois",
        amount: "700 $",
        headline: "dans la cagnotte du classement",
        body: "Le top 10 se partage la cagnotte chaque mois.",
        ctaLabel: "Voir le classement →",
      },
    },
  },
  {
    id: "signup-25",
    enabled: true,
    eyebrow: "Welcome offer",
    amount: "$25",
    headline: "free on signup",
    body: "No deposit required. Trade markets immediately.",
    ctaLabel: "Join now →",
    ctaHref: "/promos/welcome-25",
    icon: "gift",
    theme: "ochre",
    translations: {
      en: {
        eyebrow: "Welcome offer",
        amount: "$25",
        headline: "free on signup",
        body: "No deposit required. Trade markets immediately.",
        ctaLabel: "Join now →",
      },
      pt: {
        eyebrow: "Oferta de boas-vindas",
        amount: "R$125",
        headline: "grátis ao se cadastrar",
        body: "Sem depósito. Opere mercados na hora.",
        ctaLabel: "Cadastrar agora →",
      },
      es: {
        eyebrow: "Oferta de bienvenida",
        amount: "$500 MXN",
        headline: "gratis al registrarte",
        body: "Sin depósito. Opera mercados al instante.",
        ctaLabel: "Crear cuenta →",
      },
      fr: {
        eyebrow: "Offre de bienvenue",
        amount: "35 $",
        headline: "gratuit à l'inscription",
        body: "Aucun dépôt requis. Échangez immédiatement.",
        ctaLabel: "S'inscrire →",
      },
    },
  },
  {
    id: "refer-friend",
    enabled: true,
    eyebrow: "Refer a friend",
    amount: "$10",
    headline: "per friend that signs up",
    body: "Up to $200 in referral credit per month.",
    ctaLabel: "Invite friends →",
    ctaHref: "/promos/refer-friend",
    icon: "users",
    theme: "forest",
    translations: {
      en: {
        eyebrow: "Refer a friend",
        amount: "$10",
        headline: "per friend that signs up",
        body: "Up to $200 in referral credit per month.",
        ctaLabel: "Invite friends →",
      },
      pt: {
        eyebrow: "Indique um amigo",
        amount: "R$50",
        headline: "por amigo que se cadastrar",
        body: "Até R$1.000 em crédito de indicação por mês.",
        ctaLabel: "Convidar amigos →",
      },
      es: {
        eyebrow: "Invita a un amigo",
        amount: "$200 MXN",
        headline: "por amigo que se registre",
        body: "Hasta $4,000 MXN al mes en crédito por referidos.",
        ctaLabel: "Invitar amigos →",
      },
      fr: {
        eyebrow: "Inviter un ami",
        amount: "14 $",
        headline: "par ami inscrit",
        body: "Jusqu'à 280 $ de crédit de parrainage par mois.",
        ctaLabel: "Inviter des amis →",
      },
    },
  },
  {
    id: "first-trade-match",
    enabled: true,
    eyebrow: "First trade match",
    amount: "2×",
    headline: "your first $25 stake",
    body: "Matched up to $25 on your first trade.",
    ctaLabel: "Place first trade →",
    ctaHref: "/promos/first-trade-match",
    icon: "zap",
    theme: "magenta",
    translations: {
      en: {
        eyebrow: "First trade match",
        amount: "2×",
        headline: "your first $25 stake",
        body: "Matched up to $25 on your first trade.",
        ctaLabel: "Place first trade →",
      },
      pt: {
        eyebrow: "Primeira operação dobrada",
        amount: "2×",
        headline: "sua primeira aposta de R$125",
        body: "Até R$125 dobrados na sua primeira operação.",
        ctaLabel: "Operar agora →",
      },
      es: {
        eyebrow: "Tu primera operación, doble",
        amount: "2×",
        headline: "tu primera apuesta de $500 MXN",
        body: "Igualado hasta $500 MXN en tu primera operación.",
        ctaLabel: "Hacer primera operación →",
      },
      fr: {
        eyebrow: "Première mise doublée",
        amount: "2×",
        headline: "votre première mise de 35 $",
        body: "Jusqu'à 35 $ doublés sur votre première mise.",
        ctaLabel: "Faire une première mise →",
      },
    },
  },
];

const PROMO_STORAGE_KEY = "granbay.v6.promo-banners";

/**
 * Read the banner config from localStorage with the default array as
 * fallback. Returns the defaults on SSR (no window) and when the
 * stored value is malformed.
 */
export function loadPromoSlides(): PromoSlideConfig[] {
  if (typeof window === "undefined") return DEFAULT_SLIDES;
  try {
    const raw = window.localStorage.getItem(PROMO_STORAGE_KEY);
    if (!raw) return DEFAULT_SLIDES;
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed) || parsed.length === 0) return DEFAULT_SLIDES;
    // Best-effort validation — each row must have an id and headline.
    const valid = parsed.filter(
      (row): row is PromoSlideConfig =>
        typeof row === "object" &&
        row !== null &&
        typeof (row as PromoSlideConfig).id === "string" &&
        typeof (row as PromoSlideConfig).headline === "string",
    );
    return valid.length > 0 ? valid : DEFAULT_SLIDES;
  } catch {
    return DEFAULT_SLIDES;
  }
}

export function savePromoSlides(slides: PromoSlideConfig[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PROMO_STORAGE_KEY, JSON.stringify(slides));
    window.dispatchEvent(new CustomEvent("granbay.promo-banners.changed"));
  } catch {
    /* private mode — admin will see no persistence, but UI still updates */
  }
}

export function resetPromoSlides(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(PROMO_STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("granbay.promo-banners.changed"));
  } catch {
    /* swallow */
  }
}

/**
 * Resolve a slide's user-facing strings against the visitor's locale
 * with English fallback and finally the top-level defaults. Render
 * code calls this once per slide per visitor.
 */
export function pickPromoStrings(
  slide: PromoSlideConfig,
  locale: PromoLocale | string | null | undefined,
): {
  eyebrow: string;
  amount: string;
  headline: string;
  body: string;
  ctaLabel: string;
} {
  const trans = slide.translations;
  const code = (locale ?? "en").slice(0, 2).toLowerCase();
  const exact =
    trans && code in trans
      ? trans[code as PromoLocale]
      : undefined;
  if (exact) return exact;
  const en = trans?.en;
  if (en) return en;
  return {
    eyebrow: slide.eyebrow,
    amount: slide.amount,
    headline: slide.headline,
    body: slide.body,
    ctaLabel: slide.ctaLabel,
  };
}

/**
 * Region gate. Empty `regions` array (or missing) = show everywhere.
 * Otherwise the visitor's region must match one of the listed codes.
 */
export function slideMatchesRegion(
  slide: PromoSlideConfig,
  region: string | null | undefined,
): boolean {
  if (!slide.regions || slide.regions.length === 0) return true;
  if (!region) return true; // unknown region falls through (don't hide globally)
  return slide.regions.includes(region);
}
